import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, Loader2, Sparkles } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { PhoneInput } from "../../../components/forms/PhoneInput";
import { toast } from "sonner";
import { adminUsersService } from "../../../services/adminUsers";
import { formatApiError } from "../../../services/api";

function generatePassword() {
  // Simple, readable, meets policy (>=8 chars + digit + mixed).
  const alpha = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz";
  const nums = "23456789";
  const specials = "!@#$%&*";
  const pick = (src) => src[Math.floor(Math.random() * src.length)];
  const rest = [alpha, alpha, alpha, alpha, alpha, nums, nums, specials]
    .map(pick)
    .join("");
  return `MH${rest}`;
}

export function UserFormModal({ open, onClose, editingUser, onSaved }) {
  const { t } = useTranslation();
  const isEdit = Boolean(editingUser);
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [errors, setErrors] = useState({});
  const emptyForm = {
    email: "",
    first_name: "",
    last_name: "",
    phone: "",
    role: "standard",
    initial_password: "",
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setShowPw(false);
    if (editingUser) {
      setForm({
        email: editingUser.email || "",
        first_name: editingUser.first_name || "",
        last_name: editingUser.last_name || "",
        phone: editingUser.phone || "",
        role: editingUser.role || "standard",
        initial_password: "",
      });
    } else {
      setForm(emptyForm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingUser]);

  const validate = () => {
    const e = {};
    if (!isEdit) {
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email))
        e.email = t("admin.users.err_email");
      if (form.initial_password.length < 8 || !/\d/.test(form.initial_password))
        e.initial_password = t("admin.users.err_password");
    }
    if (!form.first_name.trim()) e.first_name = t("admin.users.err_first_name");
    if (!form.last_name.trim()) e.last_name = t("admin.users.err_last_name");
    // Phase A11: phone is optional. Only validate the digit count when a
    // number was actually entered (dial code alone does not count).
    if (form.phone) {
      const digits = form.phone.replace(/\D/g, "");
      // Trim dial-code prefix roughly by checking any 6+ additional digits
      // exist beyond the leading country code (max 3 digits).
      if (digits.length < 8) {
        e.phone = t("admin.users.err_phone");
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      // Empty phone string → null so backend clears the value.
      const phoneOut = form.phone && form.phone.replace(/\D/g, "").length >= 8
        ? form.phone
        : null;
      if (isEdit) {
        await adminUsersService.update(editingUser.id, {
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          phone: phoneOut,
          role: form.role,
        });
        toast.success(t("admin.users.saved"));
      } else {
        await adminUsersService.create({
          email: form.email.trim().toLowerCase(),
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          phone: phoneOut,
          role: form.role,
          initial_password: form.initial_password,
        });
        toast.success(t("admin.users.created"));
      }
      onSaved && onSaved();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "DUPLICATE_EMAIL") {
        setErrors((e) => ({ ...e, email: t("admin.users.err_duplicate_email") }));
      } else if (detail && typeof detail === "object" && detail.code === "LAST_ADMIN") {
        toast.error(t("admin.users.err_last_admin"));
      } else {
        toast.error(formatApiError(err, t("common.error_generic")));
      }
    } finally {
      setSaving(false);
    }
  };

  const autoGen = () => {
    // A18: generate the password but do NOT auto-copy to clipboard and
    // do NOT reveal the field. Admin can toggle the eye if they want
    // to see it; otherwise it stays hidden — the recipient gets it via
    // the credentials email dispatched right after user create.
    const pw = generatePassword();
    setForm({ ...form, initial_password: pw });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-2xl bg-surface"
        data-testid="user-form-modal"
      >
        <DialogHeader>
          <DialogTitle>
            {isEdit ? t("admin.users.edit_title") : t("admin.users.create_title")}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t("admin.users.form_description")}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="md:col-span-2 space-y-1.5">
            <Label>{t("admin.users.field_email")} *</Label>
            <Input
              type="email"
              value={form.email}
              disabled={isEdit}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="user-form-email"
              className={`bg-surface border-border-subtle ${isEdit ? "cursor-not-allowed opacity-70" : ""}`}
            />
            {isEdit && (
              <p className="text-xs text-text-secondary italic">
                {t("admin.users.email_readonly_hint")}
              </p>
            )}
            {errors.email && (
              <p className="text-xs text-status-unavailable" data-testid="user-form-error-email">
                {errors.email}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>{t("admin.users.field_first_name")} *</Label>
            <Input
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              data-testid="user-form-first-name"
              className="bg-surface border-border-subtle"
            />
            {errors.first_name && <p className="text-xs text-status-unavailable">{errors.first_name}</p>}
          </div>
          <div className="space-y-1.5">
            <Label>{t("admin.users.field_last_name")} *</Label>
            <Input
              value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              data-testid="user-form-last-name"
              className="bg-surface border-border-subtle"
            />
            {errors.last_name && <p className="text-xs text-status-unavailable">{errors.last_name}</p>}
          </div>

          <div className="md:col-span-2 space-y-1.5">
            <Label>
              {t("admin.users.field_phone")}{" "}
              <span className="text-xs font-normal text-text-secondary">
                ({t("admin.users.phone_optional_hint")})
              </span>
            </Label>
            <PhoneInput
              value={form.phone}
              onChange={(v) => setForm({ ...form, phone: v })}
              testIdRoot="user-form-phone"
              error={Boolean(errors.phone)}
            />
            {errors.phone && <p className="text-xs text-status-unavailable">{errors.phone}</p>}
          </div>

          <div className="md:col-span-2 space-y-1.5">
            <Label>{t("admin.users.field_role")} *</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="user-form-role" className="bg-surface border-border-subtle">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="standard" data-testid="user-form-role-standard">
                  {t("admin.users.role_standard")}
                </SelectItem>
                <SelectItem value="warehouse" data-testid="user-form-role-warehouse">
                  {t("admin.users.role_warehouse")}
                </SelectItem>
                <SelectItem value="admin" data-testid="user-form-role-admin">
                  {t("admin.users.role_admin")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {!isEdit && (
            <div className="md:col-span-2 space-y-1.5">
              <Label>{t("admin.users.field_password")} *</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    type={showPw ? "text" : "password"}
                    value={form.initial_password}
                    onChange={(e) => setForm({ ...form, initial_password: e.target.value })}
                    data-testid="user-form-password"
                    className="bg-surface border-border-subtle pr-10 font-mono"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary"
                    aria-label={showPw ? t("admin.users.hide_password") : t("admin.users.show_password")}
                    data-testid="user-form-password-toggle"
                  >
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={autoGen}
                  data-testid="user-form-password-generate"
                  className="border-border-subtle"
                >
                  <Sparkles className="mr-2 h-4 w-4" />
                  {t("admin.users.generate_password")}
                </Button>
              </div>
              <p className="text-xs text-text-secondary">
                {t("admin.users.password_hint")}
              </p>
              {errors.initial_password && (
                <p className="text-xs text-status-unavailable">{errors.initial_password}</p>
              )}
            </div>
          )}
        </div>

        <div className="mt-6 flex items-center justify-end gap-2 border-t border-border-subtle pt-4">
          <Button type="button" variant="ghost" onClick={onClose} data-testid="user-form-cancel">
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={saving}
            className="bg-charcoal text-white hover:bg-charcoal-soft focus-visible:ring-champagne"
            data-testid="user-form-submit"
          >
            {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            {isEdit ? t("common.save") : t("admin.users.create_submit")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
